/**
 * Simple logger utility for frontend application.
 * Replaces direct console usage to allow for centralized logging management.
 * In production, these could be sent to a monitoring service or disabled.
 */

const isProduction = import.meta.env.PROD;

type LogLevel = 'info' | 'warn' | 'error' | 'debug';

class Logger {
    private log(level: LogLevel, message: string, ...args: unknown[]) {
        if (isProduction && level === 'debug') {
            return;
        }

        const timestamp = new Date().toISOString();
        const prefix = `[${timestamp}] [${level.toUpperCase()}]`;

        switch (level) {
            case 'error':
                console.error(prefix, message, ...args);
                break;
            case 'warn':
                console.warn(prefix, message, ...args);
                break;
            case 'info':
                console.info(prefix, message, ...args);
                break;
            case 'debug':
                console.debug(prefix, message, ...args);
                break;
        }
    }

    info(message: string, ...args: unknown[]) {
        this.log('info', message, ...args);
    }

    warn(message: string, ...args: unknown[]) {
        this.log('warn', message, ...args);
    }

    error(message: string, ...args: unknown[]) {
        this.log('error', message, ...args);
    }

    debug(message: string, ...args: unknown[]) {
        this.log('debug', message, ...args);
    }
}

export const logger = new Logger();
